import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  type ContextType,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { AlertContext } from '../../../../contexts/alert-context';
import { type Order, OrderStatus } from '../../../../data/order';
import type { OrderOptions } from '../../../../data/order-options';
import type { Product } from '../../../../data/product';
import {
  useAllocateOrderDeal,
  useBatchSetOrderItems,
  useDeleteOrder,
  useSetOrderOptionsV2,
} from '../../../../hooks/orders';
import { useProducts } from '../../../../hooks/products';
import { useOrderEventListener } from '../../../../hooks/use-order-event-listener';
import { newerOrder, orderQueryOptions, writeOrderCache } from '../../../../query/query-options';
import { storeLayout } from '../../../../router';
import {
  classifyOrderConflict,
  type ItemUpdate,
  OrderVersionConflictError,
} from '../../../../services/orders';
import type { ConnectionStatus } from '../../../../websocket/messages';
import { useConnectionStatus } from '../../../../websocket/use-connection-status';
import type { Family } from '../data/family';
import { itemHasUserCount, type ProductOrderItem } from '../data/product-order-item';
import type { EditOrderPopulatedProperties, OrderItemsViewModel, SyncStatus } from '../types';
import { getSuggestedQuantities } from '../utils';
import { newUserFamilyId, userFamilyIdForBody } from '../utils/family-id';
import { allocateCasesByDOH } from '../utils/tier-allocator';

// The 500ms settle window for the item overlay's coalesced PATCH. There is no
// separate flush-on-blur trigger (see the brief) — v1's 16-field contract has
// no room for one, and this settle plus the unmount flush below cover the
// same "don't lose the tail" requirement.
export const FLUSH_SETTLE_MS = 500;
// Short on purpose: Reorder Settings are two selects, so one pick is one
// intent and there is no typing tail to wait for — the window only has to
// coalesce a quick second pick. v1 used its 3000ms save debounce here, which
// was invisible there (v1 recomputed rows locally); on this path the settle
// sits in front of the round trip and the row rebuild, so at 3000ms a change
// looked like nothing happened (ORD-452).
export const OPTIONS_SETTLE_MS = 300;

// Poll period while the WebSocket is down. Slow on purpose: it only has to
// bound staleness until partysocket (max 30s backoff) or the visibility
// handler brings the socket back, at which point the reconnect refetch runs.
export const DEGRADED_POLL_MS = 60_000;

// The PATCH .../items wire type. `null` clears a user override back to the
// server suggestion — a different state from an explicit 0 — so this can't
// collapse to `number | undefined` the way ProductOrderItem's fields do.
type PendingEdit = { cases: number | null; units: number | null };

// One request waiting behind the in-flight one. `key` is the coalescing
// identity: two queued requests with the same key are the same intent, so the
// newer one replaces the older.
type QueuedFlush = { key: string; body: () => Promise<unknown> };

// Tracks whether a write to this resource is currently awaiting its response,
// plus the requests owed once it settles.
type FlushGuard = { inFlight: boolean; queue: QueuedFlush[] };

// The coalescing key for a hook's own repeated flushes (items, options). Each
// gets its own guard, so one shared constant is enough. Allocate uses a
// per-family key instead — see allocateDeal.
const FLUSH_KEY = 'flush';

/**
 * Run `body` under a one-at-a-time guard, so at most one write per resource is
 * ever outstanding.
 *
 * Serializing is what makes the "a successful response overwrites a newer
 * edit" race structurally impossible rather than merely unlikely: with two
 * writes in flight, the earlier one resolving last still wins the cache
 * write, and with the overlay already cleared by the later one nothing
 * re-applies the newer value — the row silently reverts, with no error.
 *
 * A call made while one is outstanding is queued and run the instant the
 * outstanding one settles. Leaving it to the next debounce cycle instead would
 * strand the tail if the user stopped typing.
 *
 * The queue holds BODIES, not a bare "one more is owed" flag. The items guard
 * carries two kinds of write — the coalesced items PATCH and a deal allocate
 * (see allocateDeal) — and a flag can only re-run whichever body happened to
 * be executing, so a tier click landing during an items PATCH would re-run the
 * PATCH and drop the allocate entirely, silently. Repeated requests still
 * collapse: a new request coalesces onto the queued TAIL when the keys match,
 * which is exactly the old flag's behaviour whenever items are the only writer
 * (the queue then never exceeds one entry). Coalescing onto the tail only —
 * rather than anywhere in the queue — is what keeps an edit typed after a tier
 * click from being reordered ahead of that click's allocate and then
 * overwritten by its response.
 */
async function runGuarded(
  guard: FlushGuard,
  key: string,
  body: () => Promise<unknown>
): Promise<void> {
  if (guard.inFlight) {
    const tail = guard.queue.at(-1);
    if (tail?.key === key) {
      tail.body = body;
    } else {
      guard.queue.push({ key, body });
    }

    return;
  }

  guard.inFlight = true;
  try {
    await body();
  } finally {
    guard.inFlight = false;
    const next = guard.queue.shift();
    if (next) {
      void runGuarded(guard, next.key, next.body);
    }
  }
}

/**
 * The shared engine for the two flush paths (items, options). Each call gets
 * its own FlushGuard — items and options PATCH different endpoints and may
 * overlap — but the lifecycle around the guard is identical and subtle, so it
 * lives here once: snapshot the pending value, send it, then `confirm` — keep
 * only what the user re-typed while the request was in flight.
 *
 * On success the confirmed value is written to `stateRef` SYNCHRONOUSLY, not
 * only through `setState`: runGuarded's `finally` can re-run the body with no
 * `await` in between, and that re-run reads `stateRef.current` before React
 * has re-rendered from the `setState` call. A functional update
 * (`setState((current) => ...)`) would look right but isn't — the follow-up
 * flush would read the pre-confirm value and re-send what this one just
 * confirmed. Do not "simplify" the ref write away; it silently reintroduces
 * that redundant PATCH.
 *
 * Returns the guarded `flush`, the unguarded `flushNow`, and `run` — the guard
 * as an operation, so a caller can compose a multi-step write on the same
 * resource without ever holding the guard object — see allocateDeal.
 */
function useGuardedFlush<Snapshot, Pending>(config: {
  stateRef: { current: Pending };
  setState: (next: Pending) => void;
  // Reads the value to send off the current pending state; null = nothing to
  // flush. Must COPY any mutable value — the snapshot has to survive later
  // pending-state writes, including the ones its own confirm performs.
  snapshot: (current: Pending) => Snapshot | null;
  send: (snapshot: Snapshot) => Promise<unknown>;
  // The pending state left over once `sent` is confirmed by the server.
  confirm: (current: Pending, sent: Snapshot) => Pending;
  onError: (error: unknown) => void;
}): {
  flush: () => Promise<void>;
  flushNow: () => Promise<boolean>;
  run: (key: string, body: () => Promise<unknown>) => Promise<void>;
} {
  const guard = useRef<FlushGuard>({ inFlight: false, queue: [] });
  // Read through a ref inside the flush body so the returned callbacks keep a
  // stable identity while always seeing the latest config.
  const configRef = useRef(config);
  configRef.current = config;

  // The flush body WITHOUT the guard, for a caller that already holds it.
  // Going back through `flush` from inside the guard would not run anything —
  // it would see `inFlight` and merely queue a follow-up, then resolve
  // immediately, so an `await` on it would be a false completion signal.
  // Reports whether the send succeeded (nothing to flush counts as success) so
  // a composed write can stop instead of piling a second failure onto a 409
  // that has already navigated the user away.
  const flushNow = useCallback(async () => {
    const { stateRef, setState, snapshot, send, confirm, onError } = configRef.current;
    const sent = snapshot(stateRef.current);
    if (sent === null) {
      return true;
    }

    try {
      await send(sent);
      const next = confirm(stateRef.current, sent);
      stateRef.current = next;
      setState(next);

      return true;
    } catch (error) {
      onError(error);

      return false;
    }
  }, []);

  const run = useCallback(
    (key: string, body: () => Promise<unknown>) => runGuarded(guard.current, key, body),
    []
  );
  const flush = useCallback(() => run(FLUSH_KEY, flushNow), [run, flushNow]);

  return { flush, flushNow, run };
}

/**
 * Dev-only: v2's suggestions come from the server on each item, so this never
 * runs in production and never reaches Datadog RUM. It exists so a real flipped
 * store can be driven in dev against live data with any divergence from the
 * frozen client-side math (getSuggestedQuantities) surfaced in the console.
 *
 * Runs from its own effect, not from buildRows: the compare needs `options`,
 * and threading options into the row build would put the 13k-row walk back on
 * the Reorder Settings edit path.
 *
 * Temporary, like warnOnAllocationDivergence below — Plan 5 deletes both along
 * with the client math.
 */
function warnOnSuggestionDivergence(
  order: Order,
  products: Product[],
  options: OrderOptions
): void {
  if (!import.meta.env.DEV) {
    return;
  }

  const productsById = new Map(products.map((product) => [product.id, product]));
  for (const item of order.items) {
    const product = productsById.get(item.product.id);
    if (!product) {
      continue;
    }

    const clientSide = getSuggestedQuantities(product, options);
    if (
      clientSide.suggested_cases !== item.suggested_cases ||
      clientSide.suggested_units !== item.suggested_units
    ) {
      console.warn(
        `[order-edit-v2] suggestion divergence for ${product.id}: server ` +
          `${item.suggested_cases}c/${item.suggested_units}u vs client ` +
          `${clientSide.suggested_cases}c/${clientSide.suggested_units}u`
      );
    }
  }
}

/**
 * Dev-only: run the client allocator on the same inputs and report any
 * disagreement with what the server actually wrote. `pkg/ordermath.AllocateByDOH`
 * is a port of `allocateCasesByDOH`, kept honest by a parity test, but nothing
 * guarded the two against drifting apart on real catalog data until now.
 */
function warnOnAllocationDivergence(family: Family, targetCases: number, server: Order): void {
  if (!import.meta.env.DEV) {
    return;
  }

  const { allocated, skipped } = allocateCasesByDOH(family.members, targetCases);
  // Skipped members expect an explicit 0 — that's the "replace" form both
  // sides now implement (useTierAllocator, and applyAllocateDeal since ORD-415).
  const client = new Map<string, number>();
  for (const { orderItem, cases } of allocated) {
    client.set(orderItem.product.id, cases);
  }

  for (const item of skipped) {
    client.set(item.product.id, 0);
  }

  const serverById = new Map(server.items.map((item) => [item.product.id, item]));
  for (const [productId, expected] of client) {
    const got = serverById.get(productId)?.cases;
    if (got !== expected) {
      console.warn(
        `[order-edit-v2] allocation divergence for ${productId} in family ` +
          `${family.id} at ${targetCases} cases: server ${got ?? 'unset'} vs client ${expected}`
      );
    }
  }
}

/**
 * Build the per-product working rows from the cached order. This is
 * `initOrderItems` (`data/product-order-item.ts`) minus `getSuggestedQuantities`
 * — v2's suggestions arrive from the server on every item, which is also why
 * this takes no `options`: nothing in a v2 row is derived client-side from
 * them. Keeping options out of the signature is what lets a Reorder Settings
 * edit (new `options` identity per field change) skip this walk entirely.
 *
 * Maps over `products` (the full vendor catalog), not `order.items` (sparse):
 * mapping the other way would drop every product the server has no item for.
 *
 * The overlay is deliberately NOT applied here: on a 13k-product store this
 * walk runs only when the server order or the catalog change, while the
 * overlay changes on every keystroke. `applyOverlay` below is the
 * per-keystroke half.
 */
function buildRows(order: Order, products: Product[]): ProductOrderItem[] {
  const itemsByProductId = new Map(order.items.map((item) => [item.product.id, item]));

  return products.map((product) => ({ ...itemsByProductId.get(product.id), product }));
}

/**
 * Overlay the pending edits onto the built rows, last, so a typed value wins
 * over whatever the cache holds — including a refetch that lands mid-typing.
 *
 * Rows with no pending edit keep their identity, matching v1's `replaceItem`
 * (`data/product-order-item.ts`): a keystroke allocates one row object, not
 * one per product in the catalog.
 */
function applyOverlay(
  rows: ProductOrderItem[],
  overlay: Map<string, PendingEdit>
): ProductOrderItem[] {
  if (overlay.size === 0) {
    return rows;
  }

  return rows.map((row) => {
    const edit = overlay.get(row.product.id);
    if (!edit) {
      return row;
    }

    return { ...row, cases: edit.cases ?? undefined, units: edit.units ?? undefined };
  });
}

// Drop overlay entries that were just confirmed by a successful flush — but
// only the ones the user hasn't re-typed since the flush started. Comparing
// values (not just deleting every key the flush covered) is what keeps an
// edit made during an in-flight request from being silently clobbered by
// that request's own success.
function dropConfirmed(
  current: Map<string, PendingEdit>,
  flushed: Map<string, PendingEdit>
): Map<string, PendingEdit> {
  const next = new Map(current);

  for (const [productId, sent] of flushed) {
    const latest = next.get(productId);
    if (latest && latest.cases === sent.cases && latest.units === sent.units) {
      next.delete(productId);
    }
  }

  return next;
}

type FlushErrorContext = {
  error: unknown;
  storeId: string;
  orderId: string;
  queryClient: QueryClient;
  navigate: ReturnType<typeof useNavigate>;
  addErrorAlert: ContextType<typeof AlertContext>['addErrorAlert'];
};

// The order was submitted or cancelled elsewhere: leave the editor for the
// summary. Reached two ways — a write's ORDER_NOT_DRAFT 409, and a refresh
// (push, reconnect, degraded poll) that lands a non-draft order — so the
// navigation and the alert live once. Mirrors v1's refreshOrder
// (use-order-items.ts:288-296).
function leaveNonDraftOrder({
  storeId,
  orderId,
  navigate,
  addErrorAlert,
}: Pick<FlushErrorContext, 'storeId' | 'orderId' | 'navigate' | 'addErrorAlert'>): void {
  navigate({
    to: '/stores/$storeId/orders/$orderId/summary',
    params: { storeId, orderId },
  });
  addErrorAlert('Order has been submitted and can no longer be edited.', {
    keepAfterRouteChange: true,
  });
}

// Both v2 mutations that flush from this hook (items, options) can fail with
// the same two 409s, and both need the same recovery — shared here so the two
// flush functions don't duplicate the discriminator (project CLAUDE.md: reuse
// before you write; a pre-push hook blocks exactly this kind of duplication).
function handleFlushError({
  error,
  storeId,
  orderId,
  queryClient,
  navigate,
  addErrorAlert,
}: FlushErrorContext): void {
  if (error instanceof OrderVersionConflictError) {
    // Version-conflict exhaustion: the response IS the server's current
    // truth. The mutation's onError deliberately skips toasting AND this
    // cache write for exactly this error (see useOrderMutationV2 in
    // hooks/orders.ts), so do it here, and leave the overlay alone — buildRows
    // re-applies it on top. No re-PATCH: the next trigger (next edit, or
    // unmount) resends it.
    //
    // writeOrderCache lands it in both caches through the newerOrder version
    // gate, same as every other order write: a 409 on one guard can be in
    // flight alongside a SUCCESSFUL, newer response on the other guard (items
    // vs options), and if that success lands first, this rebase must not
    // regress the cache below it.
    writeOrderCache(queryClient, storeId, orderId, error.latest);

    return;
  }

  if (classifyOrderConflict(error) === 'not-draft') {
    // The other 409, ORDER_NOT_DRAFT: the order was submitted or cancelled
    // elsewhere. Only this code navigates — a 409 we don't recognize
    // classifies as null and gets the mutation factory's generic toast, so a
    // new conflict cause can't silently bounce the user off an editable
    // order. The mutation factory also skips its generic toast for this
    // status (hooks/orders.ts), so this is the only alert the user sees for
    // it.
    leaveNonDraftOrder({ storeId, orderId, navigate, addErrorAlert });

    return;
  }

  // 400 validation or network failure: nothing to do here. The mutation
  // factory already toasted and reported to Datadog, so the overlay stays
  // untouched and the next trigger (next keystroke, or unmount) retries it.
  // Not scheduling a retry here is what keeps a persistently-failing endpoint
  // from becoming an unbounded settle-interval retry loop.
}

// A dropped socket outranks a pending save: the write is not going anywhere
// until the network is back, and telling the user why is the chip's job.
function liveSync(
  connection: ConnectionStatus,
  savePending: boolean
): Extract<SyncStatus, { kind: 'live' }> {
  const state = (() => {
    if (connection !== 'open') {
      return connection === 'closed' ? 'offline' : 'connecting';
    }

    return savePending ? 'saving' : 'live';
  })();

  return { kind: 'live', state, pollMs: DEGRADED_POLL_MS };
}

export function useOrderItemsV2(properties: EditOrderPopulatedProperties): OrderItemsViewModel {
  const { store, products } = properties;
  const orderId = properties.order.id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addErrorAlert } = useContext(AlertContext);
  const { orderService } = storeLayout.useRouteContext();

  // Liveness: order_updated pushes and socket reconnects invalidate the query
  // below; nothing polls while the socket is open.
  useOrderEventListener(store.id, orderId);

  const connection = useConnectionStatus();
  const { data } = useQuery({
    ...orderQueryOptions(store.id, orderId),
    // Degraded mode only: while the socket is closed no push can arrive, so
    // poll slowly; `false` at steady state. refetchIntervalInBackground
    // defaults to false, so a hidden tab does not poll either; the provider
    // force-reconnects the moment it is foregrounded.
    refetchInterval: connection === 'closed' ? DEGRADED_POLL_MS : false,
    queryFn: async () => {
      // Match the codebase's established useQuery error convention (see
      // useOrders in hooks/orders.ts): toast, then rethrow so TanStack keeps
      // its own error/retry state. Without this, a failed/offline fetch left
      // `data` undefined with no toast at all — the page just kept editing
      // silently against `properties.order`, possibly forever.
      const fetched = await orderService
        .getOrderByStore(store.id, orderId)
        .catch((error: Error) => {
          addErrorAlert(`Error loading order: ${error.message}`, { cause: error });
          throw error;
        });

      // Guard against this GET resolving after a newer PATCH response is
      // already cached (e.g. a window-focus refetch that was in flight when a
      // save landed) — see newerOrder's comment. Without this, whichever
      // response arrives last wins the cache regardless of which is newer.
      const cached = queryClient.getQueryData<Order>(orderQueryOptions(store.id, orderId).queryKey);

      return newerOrder(cached, fetched);
    },
  });
  // OrderItemsViewModel.order is never undefined; properties.order (from the
  // orders list query) is a real order, just possibly one refetch stale.
  // Deliberately not `initialData` — TanStack would treat that as fresh and
  // suppress the mount fetch for the whole staleTime.
  const order = data ?? properties.order;

  // Liveness parity with v1's poll: a refresh — push, reconnect, or degraded
  // poll — can land an order that was submitted or cancelled from another tab.
  // Leave the editor the way the not-draft 409 does, instead of rendering an
  // editable page over a sent order until the next write happens to fail.
  const orderStatus = order.status;
  useEffect(() => {
    if (orderStatus !== OrderStatus.Draft) {
      leaveNonDraftOrder({ storeId: store.id, orderId, navigate, addErrorAlert });
    }
  }, [orderStatus, store.id, orderId, navigate, addErrorAlert]);

  // Edits the user has typed that the server hasn't confirmed yet. Keyed by
  // product so repeated edits to one row collapse to its last value — this
  // map IS the flush payload, and it's what survives a refetch landing
  // mid-typing (buildRows applies it last, on top of whatever order is
  // cached). Kept in state, not a ref, so rows re-derive when it changes.
  const [overlay, setOverlay] = useState<Map<string, PendingEdit>>(new Map());
  const overlayRef = useRef(overlay);
  overlayRef.current = overlay;

  const [pendingOptions, setPendingOptions] = useState<OrderOptions | null>(null);
  const pendingOptionsRef = useRef(pendingOptions);
  pendingOptionsRef.current = pendingOptions;
  const options = pendingOptions ?? order.options;

  const batchSet = useBatchSetOrderItems();
  const setOrderOptionsMutation = useSetOrderOptionsV2();
  // Held through a ref for the same reason useGuardedFlush reads its config
  // through one: it keeps allocateDeal's identity stable across renders, which
  // matters because it is threaded down into the virtualized deals list.
  const allocateMutation = useAllocateOrderDeal();
  const allocateRef = useRef(allocateMutation);
  allocateRef.current = allocateMutation;
  // Allocate is the one write with no overlay entry standing for it, so it
  // reports itself for the Saving… indicator. A local flag rather than a
  // mutation-cache subscription: the overlay and pending options already
  // cover the items and options windows end to end, and subscribing would
  // add a second render pass on every one of those far more common writes.
  const [allocating, setAllocating] = useState(false);
  const { updateProduct, saving: productSaving } = useProducts();
  const deleteOrder = useDeleteOrder();

  // Two stages on purpose — see buildRows. The expensive walk (13k products)
  // is keyed on the server data alone; only the cheap overlay patch re-runs
  // while the user types, and options edits touch neither stage.
  const baseRows = useMemo(() => buildRows(order, products), [order, products]);
  const orderItems = useMemo(() => applyOverlay(baseRows, overlay), [baseRows, overlay]);

  useEffect(() => {
    warnOnSuggestionDivergence(order, products, options);
  }, [order, products, options]);

  const handleError = useCallback(
    (error: unknown) =>
      handleFlushError({ error, storeId: store.id, orderId, queryClient, navigate, addErrorAlert }),
    [store.id, orderId, queryClient, navigate, addErrorAlert]
  );

  const {
    flush,
    flushNow: flushItemsNow,
    run: runOnItems,
  } = useGuardedFlush<Map<string, PendingEdit>, Map<string, PendingEdit>>({
    stateRef: overlayRef,
    setState: setOverlay,
    snapshot: (current) => (current.size === 0 ? null : new Map(current)),
    send: (snapshot) =>
      batchSet.mutateAsync({
        storeId: store.id,
        orderId,
        updates: [...snapshot].map(
          ([product_id, edit]): ItemUpdate => ({
            product_id,
            cases: edit.cases,
            units: edit.units,
          })
        ),
      }),
    // Drop only what this flush covered AND the user hasn't re-typed since —
    // see dropConfirmed. The mutation's onSuccess already wrote the response
    // into the order cache, so `order` picks it up on its own.
    confirm: dropConfirmed,
    onError: handleError,
  });

  const debouncedFlush = useDebouncedCallback(flush, FLUSH_SETTLE_MS);

  const setOrderItem = useCallback(
    (item: ProductOrderItem) => {
      setOverlay((current) => {
        const next = new Map(current);
        next.set(item.product.id, { cases: item.cases ?? null, units: item.units ?? null });

        return next;
      });
      debouncedFlush();
    },
    [debouncedFlush]
  );

  /**
   * Allocate a tier server-side: the endpoint recomputes the family with
   * `pkg/ordermath.AllocateByDOH` and returns the authoritative order.
   *
   * Runs on the ITEMS guard, because it writes items. Without that, an
   * allocate POST and an items PATCH are two concurrent writers to the same
   * resource, and the older response landing last wins the cache write — the
   * exact race the guard exists to make impossible.
   *
   * Being inside the guard is also what lets step one be correct: the user's
   * typed-but-unsettled edits must reach the server BEFORE it recomputes the
   * family, so the debounce is cancelled and the overlay flushed inline via
   * `flushItemsNow`. Calling the guarded `flush` here would deadlock the
   * intent instead — it would queue a follow-up and resolve immediately.
   *
   * Keyed per family so a second click on the same tier bar while a write is
   * outstanding replaces the queued one instead of running both — the same
   * last-intent-wins collapse repeated keystrokes get, and the response of the
   * first click would only have been overwritten by the second's anyway.
   * Clicks on different families keep their own entries, in order.
   *
   * The overlay is deliberately left alone. Entries the pre-flush covered were
   * dropped by `dropConfirmed`; anything typed DURING the allocate wins by the
   * overlay rule, the same as under any other refetch. The mutation's
   * `onSuccess` writes both caches through `newerOrder`, so there is nothing to
   * apply here.
   *
   * Unlike an items edit this does NOT bump the order's edit count — allocate
   * was never meant to count as an edit (Plan 3), which riding the items PATCH
   * had silently reversed.
   */
  const allocateDeal = useCallback(
    (family: Family, targetCases: number) => {
      debouncedFlush.cancel();

      void runOnItems(`allocate:${family.id}`, async () => {
        // A failed pre-flush has already run its own recovery — a version
        // rebase, or a navigate-away on a submitted order. Allocating anyway
        // would stack a second failure (and a second alert) on top of it.
        if (!(await flushItemsNow())) {
          return;
        }

        setAllocating(true);
        try {
          const updated = await allocateRef.current.mutateAsync({
            storeId: store.id,
            orderId,
            familyId: family.id,
            cases: targetCases,
          });
          warnOnAllocationDivergence(family, targetCases, updated);
        } catch (error) {
          handleError(error);
        } finally {
          setAllocating(false);
        }
      });
    },
    [debouncedFlush, flushItemsNow, runOnItems, store.id, orderId, handleError]
  );

  // The options guard stays independent of the items one: options PATCH a
  // different endpoint and may overlap (both responses are version-gated
  // through newerOrder). Allocate is NOT in that category — it writes items —
  // which is why it shares the items guard above.
  const { flush: flushOptions } = useGuardedFlush<OrderOptions, OrderOptions | null>({
    stateRef: pendingOptionsRef,
    setState: setPendingOptions,
    snapshot: (current) => current,
    send: (snapshot) =>
      setOrderOptionsMutation.mutateAsync({ storeId: store.id, orderId, options: snapshot }),
    // Same "only if unchanged since" rule as dropConfirmed: clear only if the
    // user hasn't picked newer options while the PATCH was in flight.
    confirm: (current, sent) => (current === sent ? null : current),
    onError: handleError,
  });

  const debouncedFlushOptions = useDebouncedCallback(flushOptions, OPTIONS_SETTLE_MS);

  const setOptions = useCallback(
    (nextOptions: OrderOptions) => {
      setPendingOptions(nextOptions);
      debouncedFlushOptions();
    },
    [debouncedFlushOptions]
  );

  // Fire-and-forget on unmount (navigate away, tab close): the component is
  // going away, but a typed-and-not-yet-settled edit must not be lost. A ref
  // latch (mirroring use-order-items.ts's refreshOrderRef) is required here
  // because a plain `useEffect(() => () => flush(), [flush])` cleanup fires on
  // every identity change of `flush`, not just unmount.
  const flushRef = useRef(flush);
  flushRef.current = flush;
  const flushOptionsRef = useRef(flushOptions);
  flushOptionsRef.current = flushOptions;

  useEffect(() => {
    return () => {
      void flushRef.current();
      void flushOptionsRef.current();
    };
  }, []);

  // family_id lives on Product, not OrderItem, so it's persisted via
  // updateProduct (React Query cache patch), same as v1. Unlike v1, no local
  // orderItems patch is needed afterward: rows derive from `products`, which
  // the products cache patch already updated.
  const patchProductFamilyId = useCallback(
    async (product: Product, nextFamilyId: string) => {
      await updateProduct({ ...product, family_id: nextFamilyId });
    },
    [updateProduct]
  );

  const joinProductToFamily = useCallback(
    (product: Product, familyBody: string) =>
      patchProductFamilyId(product, userFamilyIdForBody(familyBody)),
    [patchProductFamilyId]
  );

  const removeProductFromFamily = useCallback(
    (product: Product) => patchProductFamilyId(product, newUserFamilyId()),
    [patchProductFamilyId]
  );

  // The header indicator's state. "Saving" starts on the edit itself (an
  // overlay entry, or pending options), not when the settle elapses — with a
  // 300ms settle plus the round trip, tracking only the in-flight request
  // would barely flash. Each overlay entry and the pending options live until
  // the server confirms them, so they cover the whole pending-and-in-flight
  // window; `allocating` covers the one write that has neither.
  //
  // A write that failed outright (400, network) leaves its overlay entry for
  // the next retry, so the chip stays on Saving… until that retry — the
  // mutation's toast is what reports the failure itself.
  const sync = liveSync(connection, overlay.size > 0 || pendingOptions !== null || allocating);

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const promptDeleteOrder = useCallback(() => {
    setShowCancelConfirm(true);
  }, []);

  const dismissCancelConfirm = useCallback(() => {
    setShowCancelConfirm(false);
  }, []);

  const confirmDeleteOrder = useCallback(() => {
    setShowCancelConfirm(false);
    deleteOrder(order).then((success) => {
      if (success) {
        navigate({
          to: '/stores/$storeId/orders',
          params: { storeId: order.store_id },
        });
      }
    });
  }, [deleteOrder, navigate, order]);

  const resetOrder = useCallback(() => {
    const userItems = orderItems.filter(itemHasUserCount);
    if (userItems.length === 0) {
      return;
    }

    const response = confirm(
      `This will reset changes you have made to ${userItems.length} item(s) this order. Are you sure you want to continue?`
    );
    if (!response) {
      return;
    }

    // Clear every touched product's overlay entry in one Map, matching
    // setOrderItem's own mapping of `undefined` -> `null` (the PATCH wire
    // value that clears a user override back to the server suggestion) —
    // building one Map and flushing once avoids cloning the whole overlay
    // once per touched item, which on a large manually-edited order meant a
    // full Map copy per row inside this synchronous handler.
    setOverlay((current) => {
      const next = new Map(current);
      for (const orderItem of userItems) {
        next.set(orderItem.product.id, { cases: null, units: null });
      }

      return next;
    });
    debouncedFlush();
  }, [orderItems, debouncedFlush]);

  return {
    order,
    orderItems,
    setOrderItem,
    allocateDeal,
    options,
    setOptions,
    joinProductToFamily,
    removeProductFromFamily,
    productSaving,
    // Rows derive synchronously from the cache + overlay — no seed to wait on.
    calculating: false,
    resetOrder,
    showCancelConfirm,
    promptDeleteOrder,
    dismissCancelConfirm,
    confirmDeleteOrder,
    sync,
  };
}
